import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Typography,
  Grid,
  Card,
  CardContent,
  SelectField,
  Option,
  Accordion,
  AccordionItem,
  AccordionSummary,
  AccordionTitle,
  AccordionDetails,
  Tabs,
  Tab,
  TabPanel,
  TabPanels,
  TabList,
  Dropdown,
  DropdownButton,
  DropdownMenu,
  DropdownItem,
  IconButton,
  Switch,
  FormControlLabel,
  Tooltip,
} from "@deere/fuel-react";
import SelfServiceButtons from "../SelfServiceButtons";
import { ReactComponent as DeleteIcon } from "@deere/ux.brand-foundations/icons/delete.svg";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import DetailsTab from "../tabs/QuestionStep.Details";
import SelectionsTab from "../tabs/QuestionStep.Selections";
import DependencyTab from "../tabs/QuestionStep.Dependencies";
import TranslationsTab from "../tabs/QuestionStep.Translations";
import HelpTextTranslationsTab from "../tabs/QuestionStep.HelpText.Translations";
import SelectionsTranslationsTab from "../tabs/QuestionStep.Selections.Translations";
import { SELF_SERVICE_QUE_TYPES_GENERIC, SELF_SERVICE_QUE_TYPES_PART, SELFSERVICE_FIELD_NAMES, SURVEYPART_SECTION_TYPES, TOOLTIP_TRANSLATION_LABEL, NO_INFO, QUESTION_TYPES, LBL_ERROR, SAVING_DATA, CRS_SURVEY_TEMPLATES } from "../../../../utils/surveyLiterals";
import { validateDefaultColumns, validateQuestionStep } from "../../../../utils/validation";
import QuestionLinksTab from "../tabs/QuestionStep.Links";
import { DndContext } from "@dnd-kit/core";
import Droppable from "../utils.js/Droppable";
import { ERR_ADD_CNTRY_QUE, ERR_DEPENDENCY_REORDER } from "../../../../utils/errorLiterals";
import { GenericFuelModal } from "../../../../components/modals/GenericFuelModal";
import SelfServiceLoader from "../SelfServiceLoader";
import PartTableColumns from "../components/PartTableColumns";

const QuestionStep = ({questionStepError, setQuestionStepError, currentStep, activeStep, setActiveStep, payloadData, setPayloadData, languageList }) => {
  const { t } = useTranslation();
  const [selectedSec, setSelectedSec] = useState("");
  const [questions, setQuestions] = useState([]);
  const section = payloadData.sections.find((sec) => sec.code === selectedSec);
  const [isPartSection, setIsPartSection] = useState(false);
  const [showReorderFeature, setShowReorderFeature] = useState(false);
  const [showQueTypeError, setShowQueTypeError] = useState(false);
  const selfServiceQueTypes = section && section.type === SURVEYPART_SECTION_TYPES.PART ? SELF_SERVICE_QUE_TYPES_PART : SELF_SERVICE_QUE_TYPES_GENERIC;
  const isPartTemplate = payloadData?.templateType === CRS_SURVEY_TEMPLATES.PART;
   const [loader, setLoader] = useState({isLoading: false, message: ""});

  useEffect(() => {
    if (selectedSec && payloadData) {
      const questions = [...section.questions];
      questions.forEach((que) => {
        const index = selfServiceQueTypes.findIndex((type) => type.code === que.type);
        if (index > -1) {
          que.typeText = selfServiceQueTypes[index].type;
          que.needSelections = selfServiceQueTypes[index].needSelections;  
        }
      });
      setQuestions(questions);
    }
  }, [selectedSec, payloadData.sections]);

  const addQueHandler = (quesType) => {
    let isQueTypeError = true;
    if (quesType.code === QUESTION_TYPES.SEL_STATE) { //Is the question being added a state selector
      const arrQueSelCntry = questions.filter((que) => que.type === QUESTION_TYPES.SEL_CNTRY || que.type === QUESTION_TYPES.SEL_CTY_UW);
      const arrQueSelState = questions.filter((que) => que.type === QUESTION_TYPES.SEL_STATE);
      if (arrQueSelCntry.length > 0) { //There should be atleast 1 country selector already added
        for (let i=0; i<arrQueSelCntry.length; i++) { //Checking if there is atleast 1 country selector which is not already mapped to a state selector
          const index = arrQueSelState.findIndex((que) => que.codeQuestionDepends === arrQueSelCntry[i].code);
          if (index === -1) {
            isQueTypeError = false;
            break;
          }
        }
      }
    } else isQueTypeError = false;
    if (isQueTypeError) setShowQueTypeError(true);
    else {
      let objNewQuestion = {
        type: quesType.code,
        active: true,
        idSelectionDependson: 0,
        idQuestionDepends: 0,
        codeQuestionDepends: null, 
        idQuestionParent: 0,
        selections: [],
        defaultColumns: [],
        text: "",
        showOrder: 0,
        code: "",
        required: false,
        translations: [],
        questionsGroup: 0,
        html: false,
        links: [],
        typeText: quesType.type, //Additional Property for UI //Won't be saved in DB
        needSelections: quesType.needSelections, //Additional Property for UI //Won't be saved in DB
        helpText: "",
      };
      setPayloadData((prevState) => {
        const sectionIndex = prevState.sections.findIndex((section) => section.code === selectedSec);
        const newSections = [...prevState.sections];
        const questionCount = newSections[sectionIndex].questions.length + 1;
        objNewQuestion.showOrder = questionCount;
        objNewQuestion.code = newSections[sectionIndex].code + "_Q" + questionCount;
        const newQuestions = [...newSections[sectionIndex].questions, objNewQuestion];
        newSections[sectionIndex] = { ...newSections[sectionIndex], questions: newQuestions };
        return { ...prevState, sections: newSections };
      });
    }
  };

  const handleSectionSelection =(event) => {
    payloadData.sections.every((section)=>{
      if(section.type===SURVEYPART_SECTION_TYPES.PART && section.code===event.target.value){
        setIsPartSection(true);
        return false;
      }else{
        setIsPartSection(false);
        return true;
      }
    })
    setSelectedSec(event.target.value);
  }

  const changeHandler = (e, code) => {
    setQuestionStepError({ show: false, message: "" });
    setPayloadData((prevState) => {
      const sectionIndex = prevState.sections.findIndex((section) => section.code === selectedSec);
      const newSections = [...prevState.sections];
      
      const newQuestions = [...newSections[sectionIndex].questions];
      const questionIndex = newQuestions.findIndex((que) => que.code === code);
      switch (e.target.name) {
        case SELFSERVICE_FIELD_NAMES.label:
          newSections[sectionIndex].questions[questionIndex].text = e.target.value;
          break;
        case SELFSERVICE_FIELD_NAMES.tooltip:
          newSections[sectionIndex].questions[questionIndex].helpText = e.target.value;
          break;
        case SELFSERVICE_FIELD_NAMES.questionsGroup:
          newSections[sectionIndex].questions[questionIndex].questionsGroup = e.target.value && e.target.value !== "" ? parseInt(e.target.value) : 0;
          break;
        case SELFSERVICE_FIELD_NAMES.required:
          newSections[sectionIndex].questions[questionIndex].required = e.target.checked;
          break;
        case SELFSERVICE_FIELD_NAMES.codeQuestionDepends:
          newSections[sectionIndex].questions[questionIndex].codeQuestionDepends = e.target.value;
          newSections[sectionIndex].questions[questionIndex].idSelectionDependson = 0;
          break;
        case SELFSERVICE_FIELD_NAMES.idSelectionDependson:
          newSections[sectionIndex].questions[questionIndex].idSelectionDependson = e.target.value ? parseInt(e.target.value) : 0;
          break;
      }
      return { ...prevState, sections: newSections };
    });
  };

  const queDeleteHandler = (que) => {
    setPayloadData((prev) => {
      const secIdx = prev.sections.findIndex((s) => s.code === selectedSec);
      const questions = prev.sections[secIdx].questions
        .filter((q) => q.code !== que.code)
        .map((q) => {
          const needsReorder = q.showOrder > que.showOrder;
          const updatedShowOrder = needsReorder ? q.showOrder - 1 : q.showOrder;
          const hasDependency = q.codeQuestionDepends === que.code || q.idQuestionDepends === que.id;
          return {
            ...q,
            showOrder: updatedShowOrder,
            code: needsReorder ? `${q.code.split("_Q")[0]}_Q${updatedShowOrder}` : q.code,
            ...(hasDependency && {
              idSelectionDependson: 0,
              codeQuestionDepends: null,
              idQuestionDepends: null,
            }),
          };
        });
      const sections = [...prev.sections];
      sections[secIdx] = { ...sections[secIdx], questions };
      return { ...prev, sections };
    });
  };

  const fnValidate = (publishHandler) => {
    let error = validateQuestionStep(payloadData);
    if (error.show) setQuestionStepError(error);
    else {
      error = validateDefaultColumns(payloadData);
      error.show ?
        setQuestionStepError(error) 
        : (setLoader({ isLoading: true, message: SAVING_DATA }), publishHandler(payloadData));
    }
  };

  const calculateNewIndex = (activeIndex, overIndex, index) => {
    if (activeIndex < overIndex) {
      return index < activeIndex ? index + 1 :
             index === activeIndex ? overIndex + 1 :
             index <= overIndex ? index :
             index + 1;
    } else { // activeIndex > overIndex
      return index < activeIndex ? (index < overIndex ? index + 1 : index + 2) :
             index === activeIndex ? overIndex + 1 :
             index + 1;
    }
  }

  const handleDragEnd = ({ active, over }) => {
    setQuestionStepError({ show: false, message: "" });
    if (!over) {
      return;
    }
    if (active.id !== over.id) {
      const activeIndex = active.data.current.sortable.index;
      const overIndex = over.data.current?.sortable.index || 0;
      const arrItems = active.data.current.sortable.items;
      const arrNewShowOrder = arrItems.map((item, index) => {
        return { queCode: item, showOrder: calculateNewIndex(activeIndex, overIndex, index) };
      });
      let isDependencyError = false;
      for (let i=0; i<questions.length; i++) {
        if (questions[i].codeQuestionDepends && questions[i].codeQuestionDepends !== "") {
          const queShowOrderIdx = arrNewShowOrder.findIndex((item) => questions[i].code === item.queCode);
          const parentShowOrderIdx = arrNewShowOrder.findIndex((item) => questions[i].codeQuestionDepends === item.queCode);
          if (queShowOrderIdx > -1 && parentShowOrderIdx > -1 && arrNewShowOrder[parentShowOrderIdx].showOrder > arrNewShowOrder[queShowOrderIdx].showOrder) {
            isDependencyError = true;
            break;
          }
        }
      }
      if (isDependencyError) setQuestionStepError({ show: true, message: ERR_DEPENDENCY_REORDER });
      else
        setPayloadData((prevState) => {
          const sectionIndex = prevState.sections.findIndex((section) => section.code === selectedSec);
          const newSections = [...prevState.sections];
          let newQuestions = [...newSections[sectionIndex].questions];
          newQuestions.forEach((que) => {
            const index = arrNewShowOrder.findIndex((item) => item.queCode === que.code);
            que.showOrder = arrNewShowOrder[index].showOrder;
          });
          newSections[sectionIndex].questions = newQuestions;
          return { ...prevState, sections: newSections };
        });
    }
  };

  return (
    <Card>
      <CardContent>
        {loader.message !== "" && <SelfServiceLoader loader={loader} />}
        {loader.message === "" && <Grid>
          <Typography variant="h1" mb={4}>
            {currentStep.step} - {currentStep.title}
          </Typography>
          <SelectField
            label="Select your section"
            value={selectedSec}
            onChange={(event) => {
              setQuestionStepError({ show: false, message: "" });
              handleSectionSelection(event);
            }}
          >
            <Option disabled hidden value="">
              {t("lbl_dropdown_select")}
            </Option>
            {payloadData &&
              payloadData.sections &&
              payloadData.sections.sort((a, b) => (a.showOrder > b.showOrder ? 1 : -1)).map((sec) => (
                <Option key={sec.code} value={sec.code} disabled={sec.type === SURVEYPART_SECTION_TYPES.SUPPLIER || sec.type === SURVEYPART_SECTION_TYPES.RESPONSES}>
                  {sec.description}
                </Option>
              ))}
          </SelectField>
          {isPartSection && <PartTableColumns payloadData={payloadData} setPayloadData={setPayloadData} languageList={languageList} />}
          <Grid container justifyContent="space-between" mt={4}>
            <FormControlLabel control={<Switch onChange={(e) => setShowReorderFeature(e.target.checked)} disabled={!selectedSec} />} label="Reorder Questions" />
            <Dropdown>
              <DropdownButton disabled={!selectedSec}>Add New Question</DropdownButton>
              <DropdownMenu>
                {selfServiceQueTypes.map((types) => (
                  <Tooltip title={types.helpText || NO_INFO} arrow>
                  <DropdownItem key={types.code} onClick={() => addQueHandler(types)}>
                    {types.type}
                  </DropdownItem>
                  </Tooltip>
                ))}
              </DropdownMenu>
            </Dropdown>
          </Grid>
          {isPartTemplate && (
            <Grid
              container
              alignItems="flex-start"
              columnSpacing={1}
              mt={2}
              mb={1}
              sx={{ backgroundColor: "#e8f4fd", border: "1px solid #90caf9", borderRadius: "6px", padding: "10px 14px" }}
            >
              <Grid item sx={{ pt: "2px" }}>
                <FilterAltIcon sx={{ fontSize: "1rem", color: "#1565c0" }} />
              </Grid>
              <Grid item xs>
                <Typography variant="subtitle" sx={{ fontSize: "0.875rem", color: "#1565c0" }}>
                  Questions of type <strong>Open Text, Numerical, Option and Checkbox</strong> are{" "}
                  <strong>eligible for reporting</strong> in the Report Configuration step and can be used as
                  filter conditions to limit which parts appear in the generated report.
                </Typography>
              </Grid>
            </Grid>
          )}
          {showReorderFeature ? (
            <DndContext onDragEnd={handleDragEnd}>
              <Droppable id="questions" questions={questions} />
            </DndContext>
          ) : (
            <Accordion dense mt={4} mb={4}>
              {questions
                .sort((a, b) => (a.showOrder > b.showOrder ? 1 : -1))
                .map((que) => (
                  <AccordionItem key={que.code}>
                    <Grid container>
                      <Grid item xs={11}>
                        <AccordionSummary>
                          <AccordionTitle>{que.text ? `${que.showOrder}. ${que.text}` : `${que.showOrder}`}</AccordionTitle>
                        </AccordionSummary>
                      </Grid>
                      <Grid item xs={1} textAlign="center" alignContent="center">
                        <IconButton onClick={() => queDeleteHandler(que)}>
                          <DeleteIcon />
                        </IconButton>
                      </Grid>
                    </Grid>
                    <AccordionDetails>
                      <Tabs>
                        <TabList>
                          <Tab label="Details" />
                          {que.needSelections && <Tab label="Selections" />}
                          <Tab label="Dependency" />
                          <Tab label="Links" />
                          {payloadData.language && payloadData.language.length > 1 && <Tab label="Translations" />}
                          {payloadData.language && payloadData.language.length > 1  && <Tab label={TOOLTIP_TRANSLATION_LABEL} />}
                      </TabList>
                        <TabPanels>
                          <TabPanel>
                            <DetailsTab changeHandler={changeHandler} que={que} isPartSection={isPartSection}/>
                          </TabPanel>
                          {que.needSelections && (
                            <TabPanel style={{ marginTop: "20px" }}>
                              <Tabs>
                                <TabList style={{ height: "50px" }}>
                                  <Tab label="Selections Details" />
                                  {payloadData.language && payloadData.language.length > 1 && <Tab label="Selections Translations" />}
                                </TabList>
                                <TabPanels>
                                  <TabPanel>
                                    <SelectionsTab setError={setQuestionStepError} setPayloadData={setPayloadData} payloadData={payloadData} selectedSec={selectedSec} que={que} />
                                  </TabPanel>
                                  <TabPanel>
                                    {payloadData.language && payloadData.language.length > 1  && (
                                      <SelectionsTranslationsTab
                                        payloadData={payloadData}
                                        setPayloadData={setPayloadData}
                                        selectedSec={selectedSec}
                                        que={que}
                                        payloadLanguages={payloadData.language}
                                        languageList={languageList}
                                      />
                                    )}
                                  </TabPanel>
                                </TabPanels>
                              </Tabs>
                            </TabPanel>
                          )}
                          <TabPanel>
                            <DependencyTab payloadData={payloadData} selectedSec={selectedSec} selectedQue={que} changeHandler={changeHandler} />
                          </TabPanel>
                          <TabPanel>
                            <QuestionLinksTab section={section} selectedSec={selectedSec} selectedQue={que} payloadData={payloadData} setPayloadData={setPayloadData} />
                          </TabPanel>
                        {payloadData.language && payloadData.language.length > 1 && (
                          <TabPanel>
                            <TranslationsTab payloadData={payloadData} setPayloadData={setPayloadData} selectedSec={selectedSec} que={que} languageList={languageList} />
                          </TabPanel>
                        )}
                        {payloadData.language && payloadData.language.length > 1  && (
                          <TabPanel>
                            <HelpTextTranslationsTab payloadData={payloadData} setPayloadData={setPayloadData} selectedSec={selectedSec} que={que} languageList={languageList} />
                          </TabPanel>
                        )}
                      </TabPanels>
                    </Tabs>
                  </AccordionDetails>
                </AccordionItem>
              ))}
          </Accordion>
          )}
          {questionStepError.show && (
            <Typography variant="subtitle1" color="#C21020" textAlign="right" mb={2}>
              {questionStepError.message}
            </Typography>
          )}
          <Grid container justifyContent="flex-end">
            <SelfServiceButtons activeStep={activeStep} setActiveStep={setActiveStep} fnValidate={fnValidate} payloadData={payloadData} setPayloadData={setPayloadData} setLoader={setLoader}/>
          </Grid>
        </Grid>}
      </CardContent>
      <GenericFuelModal title={LBL_ERROR} message={ERR_ADD_CNTRY_QUE} show={showQueTypeError} handleClose={() => setShowQueTypeError(false)} handleSave={() => setShowQueTypeError(false)} />
    </Card>
  );
};

export default QuestionStep;
